import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { TeamCapacityForecastData } from '../../../interfaces/metrics';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'bst-capacity-forecast',
  templateUrl: './capacity-forecast.component.html',
  styleUrls: ['./capacity-forecast.component.scss'],
  standalone: false,
})
export class CapacityForecastComponent implements OnChanges {
  @Input() data!: TeamCapacityForecastData;
  options: EChartsOption = {};
  // TeamCapacityForecastResolver always emits exactly HORIZON_WEEKS (13) rows, so
  // this only trips on a malformed/short payload, never on a real one: it means
  // "no forecast history at all", not "no variation in it".
  sparse = false;
  // A fully committed team legitimately has freeHours 0 in every week; that is
  // real information (and agrees with the hero "Fully committed" chip above this
  // chart), not missing data, so it gets its own true message instead of being
  // read as "not enough data".
  allZero = false;

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.data) { return; }
    const weeks = this.data.weeks;
    this.sparse = weeks.length < 2;
    if (this.sparse) { return; }
    // Tested on freeHours (whole hours), not freeFte: freeFte is rounded to 1
    // decimal, so any week with under 2 free hours already reads as 0.0 FTE even
    // though freeHours itself is not exactly zero.
    this.allZero = weeks.every(w => w.freeHours === 0);
    if (this.allZero) { return; }
    const last = weeks.length - 1;
    this.options = {
      animation: false,
      // top leaves room for the per-bar value labels; at 8 the tallest bar's label clips.
      grid: { left: 34, right: 8, top: 22, bottom: 20 },
      tooltip: {
        trigger: 'axis',
        textStyle: { color: '#102C45', fontSize: 12 },
        formatter: (params: any) => {
          const i = params[0].dataIndex;
          const w = weeks[i];
          const free = this.translate.instant('dashboard.team.fte_free_tip', { fte: w.freeFte, hours: w.freeHours });
          const below = w.belowCommitment > 0
            ? '<br/>' + this.translate.instant('dashboard.team.below_commitment_tip', { n: w.belowCommitment })
            : '';
          return w.weekStart + '<br/>' + free + below;
        },
      },
      xAxis: {
        type: 'category',
        data: weeks.map(w => w.weekStart),
        axisLabel: {
          color: '#6B7A89', fontSize: 11,
          interval: (idx: number) => idx === 0 || idx === last,
          formatter: (value: string, idx: number) =>
            idx === 0 ? this.translate.instant('dashboard.team.now')
                      : this.translate.instant('dashboard.team.plus_weeks', { n: last }),
        },
        axisLine: { lineStyle: { color: '#EEF1F5' } }, axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#6B7A89', fontSize: 11 },
        splitLine: { lineStyle: { color: '#EEF1F5' } },
      },
      series: [{
        type: 'bar',
        data: weeks.map(w => w.freeFte),
        itemStyle: { color: '#1863DC', borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 22,
        // Read the chart without hovering it: every non-zero bar states its own FTE.
        // Zero bars stay blank rather than printing a row of noise.
        label: {
          show: true, position: 'top', color: '#6B7A89', fontSize: 10,
          formatter: (p: any) => (p.value > 0 ? String(p.value) : ''),
        },
      }],
    };
  }
}
